import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: Date;
  fecha_creacion?: string;
  isNew?: boolean;
}

export interface ChatRequest {
  mensaje: string;
  id_conversacion: number | null;
  id_modulo: number;
  id_clase?: number;
}

export interface MensajeBackend {
  fecha_creacion: string;
  id_mensaje: number;
  id_conversacion: number;
  rol: 'user' | 'assistant';
  contenido: string;
  tokens_usados?: number;
  modelo_usado?: string | null;
  metadatos?: any;
}

export interface ConversacionBackend {
  id_conversacion: number;
  titulo: string;
}

export interface ChatResponse {
  ok: boolean;
  msg?: string;
  conversacion?: ConversacionBackend;
  mensaje_usuario?: MensajeBackend;
  mensaje_asistente?: MensajeBackend;
  tokens_usados?: number;
}

export interface Conversacion {
  id_conversacion: number;
  id_usuario: number;
  activa: boolean;
  fecha_creacion: string;
  fecha_actualizacion?: string;
  ultimo_mensaje?: string;
  ultima_fecha?: string;
}

export interface ConversacionesResponse {
  ok: boolean;
  conversaciones?: Conversacion[];
  total?: number;
  pagina?: number;
  total_paginas?: number;
}

export interface MensajeConversacion {
  id_mensaje: number;
  id_conversacion: number;
  rol: 'usuario' | 'asistente';
  contenido: string;
  fecha_creacion: string;
}

export interface MensajesResponse {
  ok: boolean;
  mensajes?: MensajeConversacion[];
  total?: number;
}

@Injectable({
  providedIn: 'root'
})
export class GPTAssistantService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/asistente`;
  private currentConversationId: number | null = null;

  /**
   * Enviar mensaje al asistente
   */
  sendMessage(mensaje: string, idConversacion?: number | null, idModulo?: number, idClase?: number): Observable<ChatResponse> {
    const body: ChatRequest = {
      mensaje: mensaje.trim(),
      id_conversacion: idConversacion !== undefined ? idConversacion : (this.currentConversationId || null),
      id_modulo: idModulo || 0
    };

    if (idClase) body.id_clase = idClase;

    return this.http.post<ChatResponse>(`${this.baseUrl}/chat`, body);
  }

  /**
   * Procesar respuesta del chat
   */
  processChatResponse(response: ChatResponse): {
    userMessage: ChatMessage;
    assistantMessage: ChatMessage;
    conversationId: number | null;
    conversationTitle?: string;
  } {
    if (response.ok && response.mensaje_asistente) {
      // Actualizar el ID de conversación si se creó una nueva
      if (response.conversacion?.id_conversacion) {
        this.currentConversationId = response.conversacion.id_conversacion;
      }

      // Convertir mensajes del backend al formato del componente
      const userMessage: ChatMessage = response.mensaje_usuario ? {
        role: response.mensaje_usuario.rol === 'user' ? 'user' : 'assistant',
        content: response.mensaje_usuario.contenido,
        timestamp: new Date(response.mensaje_usuario.fecha_creacion),
        fecha_creacion: response.mensaje_usuario.fecha_creacion
      } : {
        role: 'user',
        content: '',
        timestamp: new Date()
      };

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.mensaje_asistente.contenido,
        timestamp: new Date(response.mensaje_asistente.fecha_creacion),
        fecha_creacion: response.mensaje_asistente.fecha_creacion
      };

      return {
        userMessage,
        assistantMessage,
        conversationId: response.conversacion?.id_conversacion || this.currentConversationId,
        conversationTitle: response.conversacion?.titulo
      };
    }
    throw new Error(response.msg || 'Error al procesar la respuesta del asistente');
  }

  /**
   * Obtener todas las conversaciones del usuario
   */
  obtenerConversaciones(limite: number = 20, pagina: number = 1, activa?: boolean): Observable<ConversacionesResponse> {
    let url = `${this.baseUrl}/conversaciones?limite=${limite}&pagina=${pagina}`;
    if (activa !== undefined) {
      url += `&activa=${activa}`;
    }
    return this.http.get<ConversacionesResponse>(url);
  }

  /**
   * Obtener mensajes de una conversación específica
   */
  obtenerMensajes(idConversacion: number): Observable<MensajesResponse> {
    return this.http.get<MensajesResponse>(`${this.baseUrl}/conversaciones/${idConversacion}/mensajes`);
  }

  /**
   * Crear nueva conversación (limpiar contexto)
   */
  crearNuevaConversacion(): Observable<{ ok: boolean; id_conversacion?: number; msg?: string }> {
    return this.http.post<{ ok: boolean; id_conversacion?: number; msg?: string }>(
      `${this.baseUrl}/conversaciones/nueva`,
      {}
    );
  }

  /**
   * Eliminar conversación
   */
  eliminarConversacion(idConversacion: number): Observable<{ ok: boolean; msg?: string }> {
    return this.http.delete<{ ok: boolean; msg?: string }>(
      `${this.baseUrl}/conversaciones/${idConversacion}`
    );
  }

  /**
   * Convertir mensaje del backend a ChatMessage
   */
  convertirMensajeABackend(mensaje: MensajeConversacion): ChatMessage {
    return {
      role: mensaje.rol === 'usuario' ? 'user' : 'assistant',
      content: mensaje.contenido,
      timestamp: new Date(mensaje.fecha_creacion),
      fecha_creacion: mensaje.fecha_creacion
    };
  }

  /**
   * Obtener conversación actual
   */
  getCurrentConversationId(): number | null {
    return this.currentConversationId;
  }

  /**
   * Establecer conversación actual
   */
  setCurrentConversationId(id: number | null): void {
    this.currentConversationId = id;
  }

  /**
   * Limpiar conversación actual
   */
  clearCurrentConversation(): void {
    this.currentConversationId = null;
  }

  /**
   * Cargar conversación y sus mensajes
   */
  async cargarConversacion(idConversacion: number): Promise<ChatMessage[]> {
    try {
      const response = await this.obtenerMensajes(idConversacion).toPromise();
      if (response?.ok && response.mensajes) {
        this.currentConversationId = idConversacion;
        return response.mensajes.map(msg => this.convertirMensajeABackend(msg));
      }
      return [];
    } catch (error) {
      console.error('Error al cargar conversación:', error);
      return [];
    }
  }

  /**
   * Verificar si el servicio está configurado (siempre true ahora, el backend maneja la configuración)
   */
  isConfigured(): boolean {
    return true;
  }
}
